import type { FC, ReactNode } from 'react'

import { Fragment, useRef } from 'react'
import { gsap, useGSAP } from '@/lib/gsap'
import { prefersReducedMotion } from '@/lib/motion'
import { cn } from '@/lib/utils'
import Img from '@/components/generals/img'

export type LitToken = string | { image: string, alt: string }

interface LitTextProps {
    tokens: Array<LitToken>
    className?: string
    as?: 'p' | 'h2'
}

/**
 * Copy that lights up word by word as it scrolls through the viewport,
 * with small photo pills opening up between the words.
 */
const LitText: FC<LitTextProps> = ({ tokens, className, as: Tag = 'p' }): ReactNode => {
    const root = useRef<HTMLParagraphElement>(null)

    useGSAP(() => {
        const element = root.current

        if (!element || prefersReducedMotion()) return

        const items = Array.from(element.querySelectorAll<HTMLElement>('[data-lit]'))

        gsap.set(items.filter(item => item.dataset.lit === 'word'), { opacity: 0.14 })
        gsap.set(items.filter(item => item.dataset.lit === 'pill'), { width: 0, marginRight: 0, opacity: 0 })

        const timeline = gsap.timeline({
            scrollTrigger: {
                trigger: element,
                start: 'top 82%',
                end: 'bottom 52%',
                scrub: 0.6,
                invalidateOnRefresh: true
            }
        })

        items.forEach((item, index) => {
            if (item.dataset.lit === 'pill') {
                timeline.to(item, {
                    width: () => Number.parseFloat(getComputedStyle(item).fontSize) * 2.3,
                    marginRight: () => Number.parseFloat(getComputedStyle(item).fontSize) * 0.22,
                    opacity: 1,
                    duration: 1.4,
                    ease: 'power2.out'
                }, index * 0.32)
            } else {
                timeline.to(item, { opacity: 1, duration: 0.8, ease: 'none' }, index * 0.32)
            }
        })
    }, { scope: root })

    const text = tokens.filter((token): token is string => typeof token === 'string').join(' ')
    const words: Array<{ kind: 'word', value: string } | { kind: 'pill', image: string, alt: string }> = []

    tokens.forEach(token => {
        if (typeof token === 'string') token.split(' ').filter(Boolean).forEach(value => words.push({ kind: 'word', value }))
        else words.push({ kind: 'pill', image: token.image, alt: token.alt })
    })

    return (
        <Tag ref={root} className={cn('relative', className)}>
            <span className='sr-only'>
                {text}
            </span>

            <span aria-hidden='true'>
                {words.map((item, index) => (
                    <Fragment key={index}>
                        {item.kind === 'word' ? (
                            <span data-lit='word' className='inline'>
                                {item.value}
                            </span>
                        ) : (
                            <span
                                data-lit='pill'
                                className='relative inline-block h-[0.78em] w-[2.3em] overflow-hidden rounded-full align-[-0.08em] mr-[0.22em]'
                            >
                                <Img src={item.image} alt={item.alt} sizes='160px' />
                            </span>
                        )}

                        {item.kind === 'word' && index < words.length - 1 ? ' ' : null}
                    </Fragment>
                ))}
            </span>
        </Tag>
    )
}

export default LitText
